import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtOdds, fmtPct, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ProGate } from "@/components/ProGate";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip } from "@/components/LeagueLogo";
import { StatCard } from "@/components/StatCard";
import {
  Target, TrendingUp, Flame, Award, Filter, Loader2, Sparkles, Save, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const LEAGUES = ["All", "NBA", "NFL", "NHL", "MLB", "NCAAF", "NCAAB", "EPL"];
const MARKETS = [
  { id: "", label: "All markets" },
  { id: "moneyline", label: "Moneyline" },
  { id: "spread", label: "Spread" },
  { id: "total", label: "Total" },
];
const MIN_EV_OPTS = [
  { id: 0, label: "≥ 0%" },
  { id: 3, label: "≥ +3%" },
  { id: 10, label: "≥ +10%" },
  { id: 20, label: "≥ +20%" },
];

export default function DailyPicks() {
  return (
    <ProGate feature="Daily Picks">
      <DailyPicksInner />
    </ProGate>
  );
}

function DailyPicksInner() {
  const [league, setLeague] = useState("All");
  const [market, setMarket] = useState("");
  const [minEv, setMinEv] = useState(0);
  const [picks, setPicks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackOpen, setTrackOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = { min_ev: minEv, limit: 100 };
    if (league !== "All") params.league = league;
    if (market) params.market = market;
    Promise.all([
      api.get("/picks/daily", { params }),
      api.get("/picks/daily/summary", {
        params: league !== "All" ? { league } : {},
      }),
    ]).then(([p, s]) => {
      setPicks(p.data);
      setSummary(s.data);
    }).finally(() => setLoading(false));
  }, [league, market, minEv]);

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="daily-picks-page">
      <PageHeader
        title="Daily picks"
        subtitle="Credible, model-filtered edges across every upcoming game. Longshot artifacts and miscalibrated overlays are rejected before they reach this list."
      />

      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Credible picks" value={summary?.count ?? "—"} icon={Target} accent="primary" testid="picks-kpi-count" />
        <StatCard label="Avg EV" value={summary ? `+${summary.avg_ev}%` : "—"} icon={TrendingUp} accent="green" testid="picks-kpi-avgev" />
        <StatCard label="Best pick" value={summary?.best_ev ? `+${summary.best_ev}%` : "—"} icon={Flame} accent="orange" testid="picks-kpi-best" />
        <StatCard label="High conf." value={summary?.high_confidence ?? "—"} icon={Award} accent="secondary" testid="picks-kpi-hiconf" />
      </div>

      {/* Best pick callout */}
      {summary?.best_pick && (
        <Link
          to={`/simulate?game=${summary.best_pick.game_id}`}
          className="iso-card block p-5 transition-shadow hover:shadow-card-lg"
          data-testid="best-pick-callout"
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-secondary to-secondary-dark text-white">
              <Flame className="size-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="iso-label">Highest-EV play right now</div>
              <div className="mt-0.5 flex items-center gap-2">
                <TeamLogo src={summary.best_pick.away_logo} abbr={summary.best_pick.matchup.split(" @ ")[0]} size={24} />
                <TeamLogo src={summary.best_pick.home_logo} abbr={summary.best_pick.matchup.split(" @ ")[1]} size={24} />
                <div className="font-lexend text-base font-bold text-gray-900">
                  {summary.best_pick.matchup} · {summary.best_pick.label}
                </div>
                <LeagueChip code={summary.best_pick.league} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div><div className="iso-label !mb-0">Odds</div><div className="font-lexend text-lg font-bold text-gray-900">{fmtOdds(summary.best_pick.odds)}</div></div>
              <div><div className="iso-label !mb-0">Win %</div><div className="font-lexend text-lg font-bold text-gray-900">{fmtPct(summary.best_pick.prob, 0)}</div></div>
              <div><div className="iso-label !mb-0">EV</div><div className="font-lexend text-lg font-bold text-green-dark">+{summary.best_pick.ev}%</div></div>
              <ChevronRight className="size-5 text-gray-300" />
            </div>
          </div>
        </Link>
      )}

      {/* Filters */}
      <div className="iso-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
            <Filter className="size-3.5" /> Filters
          </div>
          <FilterGroup
            testid="filter-league"
            options={LEAGUES.map((l) => ({ id: l, label: l }))}
            value={league}
            onChange={setLeague}
          />
          <span className="h-4 w-px bg-border" />
          <FilterGroup
            testid="filter-market"
            options={MARKETS}
            value={market}
            onChange={setMarket}
          />
          <span className="h-4 w-px bg-border" />
          <FilterGroup
            testid="filter-min-ev"
            options={MIN_EV_OPTS}
            value={minEv}
            onChange={setMinEv}
          />
        </div>
      </div>

      {/* Picks table */}
      <div className="iso-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-gray-500">
            <Loader2 className="mr-2 size-4 animate-spin" /> Running simulations…
          </div>
        ) : picks.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            No credible picks match these filters. Try widening the EV floor or switching leagues.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="picks-table">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Matchup</th>
                <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Pick</th>
                <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Start</th>
                <th className="px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Odds</th>
                <th className="px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Win %</th>
                <th className="px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">EV</th>
                <th className="px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">Conf.</th>
                <th className="px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {picks.map((p, i) => (
                <tr key={`${p.game_id}-${p.market}-${p.selection}`} className="hover:bg-gray-50 animate-fade-up"
                    style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}
                    data-testid={`pick-row-${i}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <TeamLogo src={p.away_logo} abbr={p.matchup.split(" @ ")[0]} size={26} />
                      <TeamLogo src={p.home_logo} abbr={p.matchup.split(" @ ")[1]} size={26} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{p.matchup}</div>
                        <div className="mt-0.5">
                          <LeagueChip code={p.league} className="!text-[10px]" />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm font-medium text-gray-900">{p.label}</td>
                  <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(p.start_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-3 text-right font-lexend text-sm font-bold text-gray-900">{fmtOdds(p.odds)}</td>
                  <td className="px-3 py-3 text-right text-sm text-gray-700">{fmtPct(p.prob, 0)}</td>
                  <td className={`px-3 py-3 text-right font-lexend text-sm font-bold ${p.ev > 0 ? "text-green-dark" : "text-red-dark"}`}>
                    {p.ev > 0 ? "+" : ""}{p.ev.toFixed(2)}%
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`iso-chip ${p.confidence === "HIGH" ? "iso-chip-green" : p.confidence === "MEDIUM" ? "iso-chip-blue" : ""}`}>
                      {p.confidence}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setTrackOpen(p)}
                        className="iso-btn-outline !py-1.5 !text-xs"
                        data-testid={`pick-track-${i}`}
                      >
                        <Save className="size-3.5" /> Track
                      </button>
                      <Link
                        to={`/simulate?game=${p.game_id}`}
                        className="iso-btn-ghost !py-1.5 !text-xs"
                        data-testid={`pick-simulate-${i}`}
                      >
                        <Sparkles className="size-3.5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {trackOpen && (
        <TrackModal pick={trackOpen} onClose={() => setTrackOpen(null)} />
      )}
    </div>
  );
}

const FilterGroup = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.id}
        onClick={() => onChange(o.id)}
        className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
          value === o.id ? "bg-primary text-primary-foreground" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
        }`}
        data-testid={`${testid}-${o.id === "" ? "all" : o.id}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const TrackModal = ({ pick, onClose }) => {
  const [stake, setStake] = useState(50);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/bets", {
        game_id: pick.game_id,
        market: pick.market,
        selection: pick.selection,
        odds: pick.odds,
        line: pick.line,
        stake,
      });
      toast.success("Bet tracked");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Failed to save");
    } finally { setBusy(false); }
  };

  const toWin = Math.round(stake * (pick.odds > 0 ? pick.odds / 100 : 100 / Math.abs(pick.odds)) * 100) / 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="pick-track-modal">
      <div className="w-full max-w-md iso-card p-6">
        <div className="iso-label">Track pick</div>
        <h3 className="mt-1 font-lexend">{pick.matchup} · {pick.label}</h3>
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span className="iso-chip">{pick.league}</span>
          <span>Odds {fmtOdds(pick.odds)}</span>
          <span>· Model {fmtPct(pick.prob, 0)}</span>
          <span className={`font-semibold ${pick.ev > 0 ? "text-green-dark" : "text-red-dark"}`}>EV {pick.ev > 0 ? "+" : ""}{pick.ev}%</span>
        </div>
        <div className="mt-4">
          <label className="iso-label">Stake ($)</label>
          <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value) || 0)} className="iso-input" data-testid="pick-stake-input" />
          <div className="mt-1.5 flex justify-between text-xs text-gray-500">
            <span>To win:</span>
            <span className="font-lexend font-bold text-gray-900">{fmtMoney(toWin)}</span>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="iso-btn-ghost" data-testid="pick-stake-cancel">Cancel</button>
          <button onClick={save} disabled={busy || !stake} className="iso-btn-primary" data-testid="pick-stake-submit">
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
